import { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "react-tooltip";
import Select from "react-select";
import "react-tooltip/dist/react-tooltip.css";
import Swal from "sweetalert2";
import {
  getServiceCallerPrescriptionsAction,
  markServiceCallerPrescriptionAsCompleteAction,
  reassignPrescriptionAction
} from "../../../../../store/actions/ServiceCallerPrescriptionAction";
import { processPayment } from "../../../../../store/actions/CounsellorActions";
import SendPaymentLinkButton from "../../../Common/SendPaymentLinkButton";
import { getDoctorsAction } from "../../../../../store/actions/DoctorsAction";
import PrescriptionFollowUp from "./ServiceCallerFollowUp.Component";
import icon2 from "../../../../../../src/assets/images/icons8-info-80.png";
import { handleSuccess, showErrorAlert } from "../../../../../services/ErrorHandlingService";

const Prescription = ({
  patientId,
  patientName,
  patientGender,
  setActiveKey,
}) => {
  const dispatch = useDispatch();

  const [showRepeatModal, setShowRepeatModal] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const doctors = useSelector((state) => state?.doctors?.doctors || [])
  const [selectDoctor, setSelectDoctor] = useState([]);
  // Prescription states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [statusFilter, setStatusFilter] = useState("ALL");
  const [loading, setLoading] = useState(false);
  const [completedPrescriptions, setCompletedPrescriptions] = useState(
    new Set()
  ); // Track completed prescriptions

  // Follow-up states
  const [showFollowup, setShowFollowup] = useState(null);
  const [selectedPrescriptionForFollowup, setSelectedPrescriptionForFollowup] =
    useState(null);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPatientForPayment, setSelectedPatientForPayment] = useState(null);

  const [amount, setAmount] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [finalAmount, setFinalAmount] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Get data from Redux store
  const { serviceCallerPrescriptions, markPrescriptionStatus } = useSelector(
    (state) =>
      state.prescriptionState || {
        serviceCallerPrescriptions: [],
        markPrescriptionStatus: null,
      }
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    if (isNaN(date)) return "-";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const isPending = (prescription) => {
    return prescription.medicines?.some(
      (m) => m.delivery_status?.toUpperCase() !== "DELIVERED"
    );
  };

  const isPaid = (p) =>
    p?.rawData?.prescription_payment_status === "PAID";

  const isPendingPayment = (p) =>
    p?.rawData?.prescription_payment_status === "PENDING";


  const isDelivered = (prescription) => {
    return prescription.medicines?.every(
      (m) => m.delivery_status?.toUpperCase() === "DELIVERED"
    );
  };

  const showCollectPayment = (p) => isPendingPayment(p);

  const showTrackProgress = (p) =>
    isPaid(p) && isDelivered(p);

  const hideAllActions = (p) =>
    isPaid(p) && !isDelivered(p);

  const getDurationLabel = (value) => {
    const month = Math.floor(value);
    const fraction = value - month;

    const days =
      fraction === 0.25 ? 7 :
        fraction === 0.5 ? 15 :
          fraction === 0.75 ? 22 : 0;

    if (month === 0) return `${days} Days`;
    if (days === 0) return `${month} Month${month > 1 ? "s" : ""}`;

    return `${month} Month${month > 1 ? "s" : ""} ${days} Days`;
  };

  const duration = Number(selectedPrescription?.duration || 0);
  const originalDuration = Number(selectedPrescription?.original_duration || duration);

  // 👉 fallback: original_duration nahi asel tar duration ghe

  const remainingDuration = originalDuration - duration;

  const remainingDurationLabel =
    remainingDuration > 0
      ? getDurationLabel(remainingDuration)
      : "Completed";
  const [newPrescription, setNewPrescription] = useState({
    medicineName: "",
    dose: "",
    frequency: "",
    quantity: "",
    howToTake: "",
  });

  useEffect(() => {
    if (patientId) {
      dispatch(getServiceCallerPrescriptionsAction({ patient_id: patientId }));
    }
  }, [dispatch, patientId]);

  // Track completed prescriptions from Redux state
  useEffect(() => {
    if (markPrescriptionStatus && markPrescriptionStatus.prescription_id) {
      setCompletedPrescriptions(
        (prev) => new Set([...prev, markPrescriptionStatus.prescription_id])
      );
    }
  }, [markPrescriptionStatus]);

  useEffect(() => {
    dispatch(getDoctorsAction()).then((res) => {
      if (res && Array.isArray(res)) {
        setSelectDoctor(res);
      }
    });
  }, [dispatch]);


  //   const prescription = selectedPrescription?.rawData || {};

  // const subtotal = parseFloat(prescription.items_subtotal || 0);
  // const discountAmount = parseFloat(prescription.discount_amount || 0);
  // const cgst = parseFloat(prescription.items_cgst_amount || 0);
  // const sgst = parseFloat(prescription.items_sgst_amount || 0);

  // const totalTax = cgst + sgst;
  // const finalPayable = parseFloat(prescription.gst_price || 0);

  // Format prescription data for display
  const formatPrescriptionData = (prescriptions) => {

    // const calc = getCalculation(prescriptions);
    if (!prescriptions || !Array.isArray(prescriptions)) {
      return [];
    }

    const filteredPrescriptions = prescriptions.filter((prescription) => {
      if (statusFilter === "ALL") return true;

      const isDelivered = prescription.medicines?.every(
        (m) =>
          m.delivery_status?.toUpperCase() === "DELIVERED"
      );

      const isPending = prescription.medicines?.some(
        (m) =>
          m.delivery_status?.toUpperCase() !== "DELIVERED"
      );

      if (statusFilter === "DELIVERED") return isDelivered;
      if (statusFilter === "PENDING") return isPending;

      return true;
    });

    return filteredPrescriptions.map((prescription) => ({

      prescription_id: prescription.prescription_id,
      prescribedBy:
        prescription.prescribed_by ||
        prescription.PrescribedBy ||
        "Unknown Doctor",
      date: new Date(prescription.created_at).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
      diseases:
        prescription.diseases?.map((d) => d.disease_name).join(", ") ||
        "No diseases listed",
      status: prescription.status || "PENDING",
      duration: prescription.duration,
      note: prescription.note || "",
      medicines: prescription.medicines || [],
      prescription_explained: prescription.prescription_explained,
      prescription_explained_date: prescription.prescription_explained_date,
      medicine_started: prescription.medicine_started,
      medicine_started_date: prescription.medicine_started_date,
      receipt_download_link: prescription.receipt_download_link,
      is_regular_service_call: prescription.is_regular_service_call,
      is_regular_followup: prescription.is_regular_followup,
      discount_amount: prescription.discount_amount, // 👈 ADD THIS
      discount_type: prescription.discount_type,
      totals: prescription.totals || {
        items_subtotal: prescription.items_subtotal || 0,
        items_cgst_amount: prescription.items_cgst_amount || 0,
        items_sgst_amount: prescription.items_sgst_amount || 0,
        items_igst_amount: prescription.items_igst_amount || 0,
        gst_price: prescription.gst_price || 0,
        igst_price: prescription.igst_price || 0,
      },
      rawData: prescription,
    }));
  };

  const getCalculation = (prescription) => {
    const subtotal = parseFloat(prescription?.totals?.items_subtotal || 0);
    const discount = parseFloat(prescription?.discount_amount || 0);

    const discounted = subtotal - discount;

    const cgstRate =
      (parseFloat(prescription?.totals?.items_cgst_amount || 0) / subtotal) * 100;

    const sgstRate =
      (parseFloat(prescription?.totals?.items_sgst_amount || 0) / subtotal) * 100;

    const cgst = discounted * (cgstRate / 100);
    const sgst = discounted * (sgstRate / 100);

    const totalTax = cgst + sgst;
    const final = discounted + totalTax;

    return { subtotal, discount, discounted, cgst, sgst, totalTax, final };
  };



  const handleCollectPayment = (prescription) => {
    setSelectedPatientForPayment({
      patient_id: patientId,
      name: patientName,
      prescription: prescription,
    });

    // optional default amount
    // setAmount(prescription?.totals?.gst_price || "");
    const calc = getCalculation(prescription);
    setAmount(Math.round(calc.final));

    setShowPaymentModal(true);
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();

    if (!amount || !paymentMode) {
      setErrors({
        paymentMode: !paymentMode ? "Required" : "",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const paymentData = {
        patient_id: selectedPatientForPayment.patient_id,
        queue_id:
          selectedPatientForPayment.prescription?.rawData?.queue_id || 0,
        amount: finalAmount || amount,
        final_amount: finalAmount || amount,
        reference_id:
          selectedPatientForPayment.prescription.prescription_id,
        coupon_code: couponCode?.trim() || "",
        payment_type: paymentMode.toLowerCase(),
        payment_for: "MEDICINE",
      };

      // 🔥 MAIN API CALL
      await dispatch(processPayment(paymentData));

      handleSuccess("Payment collected successfully");

      dispatch(
        getServiceCallerPrescriptionsAction({ patient_id: patientId })
      );

      setShowPaymentModal(false);

    } catch (err) {
      console.error(err);
      showErrorAlert("Payment failed");
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleRepeatSubmit = async () => {
    if (!selectedDoctor || !selectedPrescription) {
      Swal.fire("Warning", "Please select a doctor.", "warning");
      return;
    }

    const payload = {
      prescription_id: selectedPrescription.prescription_id,
      doctor_id: selectedDoctor,
    };

    const response = await dispatch(reassignPrescriptionAction(payload));

    if (response?.success) {
      handleSuccess(
        "Success!",
        "Prescription reassigned successfully.",
        "success"
      );

      setShowRepeatModal(false);
      setSelectedDoctor(""); // optional reset

      // Refresh prescription list
      dispatch(
        getServiceCallerPrescriptionsAction({ patient_id: patientId })
      );
    } else {
      showErrorAlert("Failed to reassign prescription.");
    }
  };

  const formattedPrescriptions = formatPrescriptionData(
    serviceCallerPrescriptions
  );

  useEffect(() => {
    setShowFollowup(null);
    setSelectedPrescriptionForFollowup(null);
  }, [statusFilter]);

  const handleViewClick = (prescription) => {
    setSelectedPrescription(prescription);
    setShowDetailModal(true);
  };

  const handleTrackProgress = (prescription) => {
    if (prescription && prescription.prescription_id) {
      setSelectedPrescriptionForFollowup(prescription);
      setShowFollowup(prescription.prescription_id);
    }
  };

  const handleMarkAsDone = async () => {
    if (!selectedPrescription) return;

    const result = await Swal.fire({
      title: "Mark as Complete?",
      text: "This prescription will be marked as completed.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, mark as done",
    });

    if (result.isConfirmed) {
      try {
        const response = await dispatch(
          markServiceCallerPrescriptionAsCompleteAction({
            prescription_id: selectedPrescription.prescription_id,
          })
        );

        if (response && response.prescription_id) {
          Swal.fire(
            "Marked!",
            "The prescription has been marked as completed.",
            "success"
          );
          setShowDetailModal(false); // Close the modal
        } else {
          Swal.fire(
            "Error!",
            "Failed to mark prescription as complete.",
            "error"
          );
        }
      } catch (error) {
        console.error("Error marking prescription as complete:", error);
        Swal.fire(
          "Error!",
          "Failed to mark prescription as complete.",
          "error"
        );
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPrescription({ ...newPrescription, [name]: value });
  };

  const handleOnSubmit = () => {
    setNewPrescription({
      medicineName: "",
      dose: "",
      frequency: "",
      quantity: "",
      howToTake: "",
    });
    setIsModalOpen(false);
  };

  const handleCloseFollowUp = () => {
    setShowFollowup(null);
    setSelectedPrescriptionForFollowup(null);
  };

  const handleAddMedicineAgain = (prescription) => {
    console.log("Add medicine again for prescription:", prescription);

    // You can open another modal here
    // or redirect to medicine-adding form.

    Swal.fire(
      "Coming Soon",
      "Add Medicine Again functionality will be implemented.",
      "info"
    );
  };
  const formatOptionLabel = ({ label, specialization }) => (
    <div>
      <div style={{ fontWeight: "bold" }}>{label}</div>
      <div style={{ fontSize: "0.8em", color: "#666" }}>{specialization}</div>
    </div>
  )

  if (loading) {
    return (
      <div className="col-xl-12">
        <div className="card shadow-none">
          <div className="card-body text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">
              Loading prescriptions for patient ID: {patientId}...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="col-xl-12">
      <div className="card shadow-none">
        <div className="card-body">
          {/* Prescription Cards */}

          <div className="d-flex justify-content-end mb-3 align-items-center gap-2">

            {/* 🔥 LABEL */}
            <label className="fw-bold mb-0">Filter:</label>

            {/* 🔥 SELECT */}
            <div style={{ width: "220px" }}>
              <Select
                value={{
                  value: statusFilter,
                  label:
                    statusFilter === "ALL"
                      ? "All"
                      : statusFilter === "DELIVERED"
                        ? "Delivered"
                        : "Pending",
                }}
                onChange={(option) => setStatusFilter(option.value)}
                options={[
                  { value: "ALL", label: "All" },
                  { value: "DELIVERED", label: "Delivered" },
                  { value: "PENDING", label: "Pending" },
                ]}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: "50px",
                    borderRadius: "10px",
                    borderColor: "#ced4da",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#0d6efd",
                    },
                  }),
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                  }),
                  multiValue: (provided) => ({
                    ...provided,
                    backgroundColor: "#f0f0f0",
                  }),
                  dropdownIndicator: (provided) => ({
                    ...provided,
                    padding: "4px",
                  }),
                  indicatorSeparator: () => ({ display: "none" }),
                }}
                classNamePrefix="select"
                isSearchable={false}
              />
            </div>

          </div>
          {formattedPrescriptions.length > 0 ? (
            <div className="row">
              {formattedPrescriptions
                .filter(
                  (prescription) =>
                    !showFollowup ||
                    prescription.prescription_id === showFollowup
                )
                .map((prescription) => {
                  const calc = getCalculation(prescription);

                  return (
                    <div
                      className="col-md-4 mb-4"
                      key={prescription.prescription_id}
                    >
                      <div
                        className="card border-0 p-3 rounded"
                        style={{
                          backgroundColor: "white",
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        <div className="card-body" style={{ position: "relative" }}>

                          <div
                            style={{
                              position: "absolute",
                              top: "10px",
                              right: "10px",
                              display: "flex",
                              gap: "6px",
                            }}
                          >
                            {prescription.is_regular_service_call === "Yes" && (
                              <span
                                className="badge"
                                style={{
                                  background: "#28a745",
                                  padding: "6px 10px",
                                  borderRadius: "20px",
                                  fontSize: "12px",
                                }}
                              >
                                Regular Service
                              </span>
                            )}

                            {prescription.is_regular_followup === "Yes" && (
                              <span
                                className="badge"
                                style={{
                                  background: "#0d6efd",
                                  padding: "6px 10px",
                                  borderRadius: "20px",
                                  fontSize: "12px",
                                }}
                              >
                                Followup
                              </span>
                            )}
                          </div>

                          <p className="fs-5 mb-0">
                            <strong>Prescribed By:</strong> {prescription.prescribedBy}
                          </p>

                          <p className="fs-5 mb-0">
                            <strong>Date:</strong>{" "}
                            {prescription?.date ? formatDate(prescription.date) : "N/A"}
                          </p>

                          <div className="d-flex justify-content-start mt-3 align-items-center gap-2">

                            {/* VIEW DETAILS */}
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleViewClick(prescription)}
                            >
                              View Details
                            </Button>

                            {/* ✅ PENDING CASE */}
                            {showCollectPayment(prescription) && (
                              <>
                                <Button
                                  variant="success"
                                  size="sm"
                                  onClick={() => handleCollectPayment(prescription)}
                                >
                                  Collect Payment
                                </Button>

                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() =>
                                    window.open(`tel:${prescription.rawData?.whatsapp_contact_no || ""}`)
                                  }
                                >
                                  <i className="fa fa-phone me-1"></i> Call
                                </Button>

                                <span data-tooltip-id={`bill-${prescription.prescription_id}`}>
                                  <i className="fa fa-info-circle text-primary ms-1" style={{ cursor: "pointer" }}></i>
                                </span>

                                <Tooltip id={`bill-${prescription.prescription_id}`} place="top">
                                  <div style={{ textAlign: "left" }}>
                                    <div>Item Total: ₹{calc.subtotal.toFixed(2)}</div>
                                    <div>Discount: ₹{calc.discount.toFixed(2)}</div>
                                    <div>After Discount: ₹{calc.discounted.toFixed(2)}</div>
                                    <div>CGST: ₹{calc.cgst.toFixed(2)}</div>
                                    <div>SGST: ₹{calc.sgst.toFixed(2)}</div>
                                    <hr />
                                    <strong>Total: ₹{Math.round(calc.final)}</strong>
                                  </div>
                                </Tooltip>
                              </>
                            )}

                            {showTrackProgress(prescription) && (
                              <Button
                                className="btn btn-info btn-sm"
                                onClick={() => handleTrackProgress(prescription)}
                              >
                                Track Progress
                              </Button>
                            )}

                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
                )}
            </div>
          ) : (
            <div className="text-center py-5">
              <h5 className="text-muted">No prescriptions found</h5>
            </div>
          )}

          {/* Follow-up Section - Shows as separate component */}
          {showFollowup && selectedPrescriptionForFollowup && (
            <PrescriptionFollowUp
              selectedPrescriptionForFollowup={selectedPrescriptionForFollowup}
              onClose={handleCloseFollowUp}
              patientId={patientId}
              patientName={patientName}
              patientGender={patientGender}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      {/* Prescription Details Modal */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Prescription Details
            <div
              style={{ fontSize: "0.9rem", color: "#6c757d", marginTop: "5px" }}
            >
              <strong>Patient:</strong> {patientName || "Unknown"} &nbsp;|&nbsp;
              <strong>Gender:</strong> {patientGender || "Unknown"} &nbsp;|&nbsp;
              <strong>Prescribed By:</strong> {selectedPrescription?.prescribedBy}
              &nbsp;|&nbsp;

              {/* 👉 NEW FIELD */}
              <strong>Remaining:</strong>{" "}
              <span
                style={{
                  color: remainingDuration > 0 ? "#dc3545" : "#28a745",
                  fontWeight: "bold",
                }}
              >
                {remainingDurationLabel}
              </span>

              {/* {selectedPrescription && (() => {
                const prescription = selectedPrescription?.totals || {};

                const raw = selectedPrescription?.rawData || {};

                const subtotal = parseFloat(selectedPrescription?.totals?.items_subtotal || 0);
                const discountAmount = parseFloat(selectedPrescription.discount_amount || 0);

                // STEP 1: after discount
                const afterDiscount = subtotal - discountAmount;

                // STEP 2: tax rate (from API)
                const cgstRate =
                  (parseFloat(selectedPrescription?.totals?.items_cgst_amount || 0) / subtotal) * 100;

                const sgstRate =
                  (parseFloat(selectedPrescription?.totals?.items_sgst_amount || 0) / subtotal) * 100;

                // ✅ Step 3 (correct tax)
                const cgst = afterDiscount * (cgstRate / 100);
                const sgst = afterDiscount * (sgstRate / 100);

                const totalTax = cgst + sgst;
                // STEP 4: final
                const finalPayable = afterDiscount + totalTax;
                // ✅ STEP 2: fallback (if not available)
                //  if (!discountAmount || discountAmount === 0) {
                //    const afterDiscount = finalPayable - totalTax;
                //    discountAmount = subtotal - afterDiscount;
                //  }

                // ✅ FINAL after discount
                //  const afterDiscount = subtotal - discountAmount;
                console.log("TOTALS", prescription);
                console.log("discount from selected", selectedPrescription.discount_amount);
                console.log("discount from raw", selectedPrescription.rawData?.discount_amount);
                console.log("FINAL SELECTED", selectedPrescription);
                return (
                  <>
                    <div className="d-flex justify-content-between">
                      <span>Item Total</span>
                      <span>₹{subtotal.toFixed(2)}</span>
                    </div>

                    <div className="d-flex justify-content-between">
                      <span>Discount</span>
                      <span>- ₹{discountAmount.toFixed(2)}</span>
                    </div>

                    <div className="d-flex justify-content-between">
                      <span>After Discount</span>
                      <span>₹{afterDiscount.toFixed(2)}</span>
                    </div>

                    <div className="d-flex justify-content-between">
                      <span>CGST</span>
                      <span>₹{cgst.toFixed(2)}</span>
                    </div>

                    <div className="d-flex justify-content-between">
                      <span>SGST</span>
                      <span>₹{sgst.toFixed(2)}</span>
                    </div>

                    <div className="d-flex justify-content-between">
                      <strong>Total Tax</strong>
                      <span>₹{totalTax.toFixed(2)}</span>
                    </div>

                    <hr />

                    <div className="d-flex justify-content-between">
                      <strong>Grand Total</strong>
                      <strong>₹{finalPayable.toFixed(2)}</strong>
                    </div>
                  </>
                );
              })()} */}
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPrescription && (
            <div
              style={{
                height: "400px",
                overflowY: "auto",
                border: "2px solid #ddd",
                borderRadius: "5px",
              }}
            >
              {(() => {
                // Separate medicines into custom and non-custom
                const regularMedicines =
                  selectedPrescription.medicines?.filter(
                    (medicine) => medicine.is_custom === "No"
                  ) || [];
                const customMedicines =
                  selectedPrescription.medicines?.filter(
                    (medicine) => medicine.is_custom === "Yes"
                  ) || [];

                return (
                  <>
                    {/* Regular Medicines Table */}
                    {regularMedicines.length > 0 && (
                      <div
                        style={{
                          border: "2px solid #ddd",
                          borderRadius: "5px",
                          marginBottom:
                            regularMedicines.length > 0 &&
                              customMedicines.length > 0
                              ? "24px"
                              : "0",
                          overflowX: "auto",
                        }}
                        className="table-responsive"
                      >
                        {regularMedicines.length > 0 &&
                          customMedicines.length > 0 && (
                            <h5 className="p-2 border-bottom mb-0">
                              Regular Medicines
                            </h5>
                          )}
                        <table
                          className="table header-border mb-0"
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            minWidth: "800px",
                          }}
                        >
                          <thead
                            style={{
                              position: "sticky",
                              top: 0,
                              backgroundColor: "#f8f9fa",
                              zIndex: 10,
                              borderBottom: "2px solid #ddd",
                            }}
                          >
                            <tr className="table-info">
                              <th
                                style={{ padding: "10px", textAlign: "center" }}
                              >
                                Medicine Name
                              </th>
                              <th
                                style={{ padding: "10px", textAlign: "center" }}
                              >
                                Dose
                              </th>
                              <th
                                style={{ padding: "10px", textAlign: "center" }}
                              >
                                Frequency
                              </th>
                              <th
                                style={{ padding: "10px", textAlign: "center" }}
                              >
                                Quantity
                              </th>
                              <th
                                style={{ padding: "10px", textAlign: "center" }}
                              >
                                Delivered Qty
                              </th>
                              <th
                                style={{ padding: "10px", textAlign: "center" }}
                              >
                                How to Take
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {regularMedicines.map((medicine, index) => (
                              <tr
                                key={medicine.prescription_medicine_id || index}
                              >
                                <td
                                  style={{
                                    padding: "10px",
                                    textAlign: "center",
                                  }}
                                >
                                  {medicine.medicine_name}
                                </td>
                                <td
                                  style={{
                                    padding: "10px",
                                    textAlign: "center",
                                  }}
                                >
                                  {medicine.dose}
                                </td>
                                <td
                                  style={{
                                    padding: "10px",
                                    textAlign: "center",
                                  }}
                                >
                                  {medicine.frequency}
                                </td>
                                <td
                                  style={{
                                    padding: "10px",
                                    textAlign: "center",
                                  }}
                                >
                                  {medicine.qty}
                                </td>
                                <td
                                  style={{
                                    padding: "10px",
                                    textAlign: "center",
                                    backgroundColor:
                                      medicine.updated_qty < medicine.qty
                                        ? "#fff3cd"
                                        : "transparent",
                                  }}
                                >
                                  {console.log(medicine)}
                                  {medicine.updated_qty}
                                  {medicine.updated_qty < medicine.qty && (
                                    <span
                                      data-tooltip-id={`medicine-tooltip-${index}`}
                                      style={{
                                        display: "inline-block",
                                        marginLeft: "8px",
                                      }}
                                    >
                                      <img
                                        src={icon2 || "/placeholder.svg"}
                                        alt="info Icon"
                                        style={{
                                          cursor: "pointer",
                                          width: "20px",
                                          height: "20px",
                                        }}
                                      />
                                      <Tooltip
                                        id={`medicine-tooltip-${index}`}
                                        place="top"
                                        style={{
                                          zIndex: 9999,
                                          maxWidth: "300px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            padding: "8px",
                                            textAlign: "left",
                                          }}
                                        >
                                          <div>
                                            <strong>Quantity:</strong>{" "}
                                            {medicine.qty}
                                          </div>
                                          <div>
                                            <strong>Delivered:</strong>{" "}
                                            {medicine.updated_qty}
                                          </div>
                                          <div
                                            style={{
                                              color: "#ffc107",
                                              marginTop: "4px",
                                            }}
                                          >
                                            <strong>Status:</strong>{" "}
                                            {medicine.delivery_status ||
                                              "pending"}
                                          </div>
                                        </div>
                                      </Tooltip>
                                    </span>
                                  )}
                                </td>
                                <td
                                  style={{
                                    padding: "10px",
                                    textAlign: "center",
                                    maxWidth: "200px",
                                  }}
                                >
                                  <small>{medicine.how_to_take}</small>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Custom Medicines Table */}
                    {customMedicines.length > 0 && (
                      <div
                        style={{
                          border: "2px solid #ddd",
                          borderRadius: "5px",
                          marginBottom: "0",
                          overflowX: "auto",
                        }}
                      >
                        {regularMedicines.length > 0 &&
                          customMedicines.length > 0 && (
                            <h5 className="p-2 border-bottom mb-0">
                              Custom Medicines
                            </h5>
                          )}
                        <table
                          className="table header-border mb-0"
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            minWidth: "800px",
                          }}
                        >
                          <thead
                            style={{
                              position: "sticky",
                              top: 0,
                              backgroundColor: "#f8f9fa",
                              zIndex: 10,
                              borderBottom: "2px solid #ddd",
                            }}
                          >
                            <tr className="table-info">
                              <th
                                style={{ padding: "10px", textAlign: "center" }}
                              >
                                Medicine Name
                              </th>
                              <th
                                style={{ padding: "10px", textAlign: "center" }}
                              >
                                Dose
                              </th>
                              <th
                                style={{ padding: "10px", textAlign: "center" }}
                              >
                                Frequency
                              </th>
                              <th
                                style={{ padding: "10px", textAlign: "center" }}
                              >
                                Quantity
                              </th>
                              <th
                                style={{ padding: "10px", textAlign: "center" }}
                              >
                                Duration
                              </th>
                              <th
                                style={{ padding: "10px", textAlign: "center" }}
                              >
                                Delivered Qty
                              </th>
                              <th
                                style={{ padding: "10px", textAlign: "center" }}
                              >
                                How to Take
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {customMedicines.map((medicine, index) => (
                              <tr
                                key={medicine.prescription_medicine_id || index}
                              >
                                <td
                                  style={{
                                    padding: "10px",
                                    textAlign: "center",
                                  }}
                                >
                                  {medicine.medicine_name}
                                </td>
                                <td
                                  style={{
                                    padding: "10px",
                                    textAlign: "center",
                                  }}
                                >
                                  {medicine.dose}
                                </td>
                                <td
                                  style={{
                                    padding: "10px",
                                    textAlign: "center",
                                  }}
                                >
                                  {medicine.frequency}
                                </td>
                                <td
                                  style={{
                                    padding: "10px",
                                    textAlign: "center",
                                  }}
                                >
                                  {medicine.qty}
                                </td>
                                <td
                                  style={{
                                    padding: "10px",
                                    textAlign: "center",
                                  }}
                                >
                                  {medicine.custom_duration_in_days || "N/A"}
                                </td>
                                <td
                                  style={{
                                    padding: "10px",
                                    textAlign: "center",
                                    backgroundColor:
                                      medicine.updated_qty < medicine.qty
                                        ? "#fff3cd"
                                        : "transparent",
                                  }}
                                >
                                  {medicine.updated_qty}
                                  {medicine.updated_qty < medicine.qty && (
                                    <span
                                      data-tooltip-id={`custom-medicine-tooltip-${index}`}
                                      style={{
                                        display: "inline-block",
                                        marginLeft: "8px",
                                      }}
                                    >
                                      <img
                                        src={icon2 || "/placeholder.svg"}
                                        alt="info Icon"
                                        style={{
                                          cursor: "pointer",
                                          width: "20px",
                                          height: "20px",
                                        }}
                                      />
                                      <Tooltip
                                        id={`custom-medicine-tooltip-${index}`}
                                        place="top"
                                        style={{
                                          zIndex: 9999,
                                          maxWidth: "300px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            padding: "8px",
                                            textAlign: "left",
                                          }}
                                        >
                                          <div>
                                            <strong>Quantity:</strong>{" "}
                                            {medicine.qty}
                                          </div>
                                          <div>
                                            <strong>Delivered:</strong>{" "}
                                            {medicine.updated_qty}
                                          </div>
                                          <div
                                            style={{
                                              color: "#ffc107",
                                              marginTop: "4px",
                                            }}
                                          >
                                            <strong>Status:</strong>{" "}
                                            {medicine.delivery_status ||
                                              "pending"}
                                          </div>
                                        </div>
                                      </Tooltip>
                                    </span>
                                  )}
                                </td>
                                <td
                                  style={{
                                    padding: "10px",
                                    textAlign: "center",
                                    maxWidth: "200px",
                                  }}
                                >
                                  <small>{medicine.how_to_take}</small>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}


                    {/* Show message if no medicines */}
                    {regularMedicines.length === 0 &&
                      customMedicines.length === 0 && (
                        <div className="text-center text-muted py-4">
                          No medicines prescribed
                        </div>
                      )}
                    {/* Add Medicine Again Button */}

                  </>
                );
              })()}
            </div>

          )}
        </Modal.Body>
        <Modal.Footer>
          {selectedPrescription &&
            isPaid(selectedPrescription) &&
            isDelivered(selectedPrescription) && (
              <div className="text-center">
                <Button
                  variant="success"
                  onClick={() => {
                    setSelectedPrescription(selectedPrescription);
                    setShowRepeatModal(true);
                  }}
                  title="Patient want same prescription again"
                >
                  Same Prescription again
                </Button>
              </div>
            )}

          {/* Only show Mark as Complete button if prescription is not completed */}
          {selectedPrescription &&
            !isPending(selectedPrescription) &&   // 🔥 ADD THIS
            !completedPrescriptions.has(selectedPrescription.prescription_id) && (
              <Button variant="info" onClick={handleMarkAsDone}>
                Mark as Complete
              </Button>
            )}
          <Button
            variant="outline-danger"
            onClick={() => setShowDetailModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      {/* ================= Repeat Prescription Modal ================= */}
      <Modal
        show={showRepeatModal}
        onHide={() => setShowRepeatModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Repeat Prescription</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="mb-3">
            <label className="form-label fw-bold">Select Doctor</label>

            <Select
              value={
                selectedDoctor
                  ? {
                    value: selectedDoctor,
                    label: doctors.find(doc => doc.doctor_id === selectedDoctor)
                      ?.doctor_name,
                    specialization:
                      doctors.find(doc => doc.doctor_id === selectedDoctor)
                        ?.specialization || "-",
                  }
                  : null
              }
              onChange={(option) => {
                setSelectedDoctor(option?.value || "");
              }}
              options={doctors.map((d) => ({
                value: d.doctor_id,
                label: d.doctor_name,
                specialization: d.specialization || "-",
              }))}
              formatOptionLabel={formatOptionLabel}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: "45px",
                  height: "45px",
                }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              menuPortalTarget={document.body}
              placeholder="Select a doctor"
              classNamePrefix="select"
              isClearable
            />
          </div>
        </Modal.Body>


        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => handleRepeatSubmit()}
            disabled={!selectedDoctor}
          >
            Submit
          </Button>

          <Button variant="outline-danger" onClick={() => setShowRepeatModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* <SendPaymentLinkButton
        patientId={selectedPatientForPayment?.patient_id}
        prescriptionId={
          selectedPatientForPayment?.prescription?.prescription_id
        }
        patientName={selectedPatientForPayment?.name}
        amount={
          finalAmount ||
          amount ||
          selectedPatientForPayment?.prescription?.totals?.items_subtotal
        }
        paymentFor="Medicine"
        paymentType="service_caller"
        className="btn me-2"
        onSuccess={() => setShowPaymentModal(false)}
      /> */}

      <Modal
        show={showPaymentModal}
        onHide={() => setShowPaymentModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Collect Payment</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <form onSubmit={handleSubmitPayment}>

            {/* AMOUNT */}
            <div className="form-group mt-3">
              <label>Amount</label>
              <input
                type="number"
                className="form-control"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </div>

            {/* PAYMENT MODE */}
            <div className="form-group mt-3">
              <label>Payment Mode</label>

              <Select
                options={[
                  { value: "Online", label: "Online" },
                  { value: "Offline", label: "Cash" },
                  { value: "UPI", label: "UPI" },
                ]}
                value={
                  paymentMode
                    ? { value: paymentMode, label: paymentMode }
                    : null
                }
                onChange={(opt) => setPaymentMode(opt?.value || "")}
              />
            </div>

            {/* 🔥 ACTION BUTTONS */}
            <div className="d-flex justify-content-between align-items-center mt-4">

              {/* ✅ SEND PAYMENT LINK */}
              <SendPaymentLinkButton
                patientId={selectedPatientForPayment?.patient_id}
                prescriptionId={
                  selectedPatientForPayment?.prescription?.prescription_id
                }
                patientName={selectedPatientForPayment?.name}
                amount={
                  finalAmount ||
                  amount ||
                  getCalculation(selectedPatientForPayment?.prescription).final
                }
                paymentFor="Medicine"
                paymentType="service_caller"
                className="btn me-2"
                onSuccess={() => setShowPaymentModal(false)}
              />

              {/* ✅ COLLECT PAYMENT */}
              <Button
                type="submit"
                style={{
                  background: "#007680",
                  border: "none",
                  padding: "6px 20px",
                  fontSize: "16px",
                  fontWeight: "500",
                  borderRadius: "6px",
                  minHeight: "50px",
                  color: "white",
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Processing...
                  </>
                ) : (
                  "Collect Payment"
                )}
              </Button>

            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div >


  );
};

export default Prescription;
